// Glow streaks and trail ribbons: the two primitives that replaced the
// instanced cubes for everything that flies (art/model-integration.md 9.5).
//
// A round in the air used to be a box. A tracer was a pale yellow bar with
// square ends, its vapour trail a translucent stick, a rocket's motor a
// flat orange plank, a salvo's contrail a ladder of see-through crates. Up
// close they read as lumber; at range they aliased into dashes. This is how
// a round is drawn now:
//
//   * StreakBatch — a capsule drawn flat to the screen between a head and a
//     tail point, round at both ends, with a soft profile across it. Its
//     "glow" flavour is light: a white-hot core in a coloured halo that lays
//     light over dark sky and tints a bright one (tracers, shell tracers,
//     rocket and missile flames, star shells). Its "soft" flavour is lit
//     vapour, alpha-blended (the faint streak every bullet drags). Its "flat"
//     flavour lies on the water instead of facing the eye (a torpedo's
//     bubble track). The capsule is built in screen space, so a streak seen
//     end-on is a round dot, never a sliver, and a streak thinner than a
//     pixel keeps a pixel's width and gives up brightness instead — a stream
//     of fire two kilometres off still reads, as faint sparks rather than
//     crawling stair-steps.
//   * TrailRibbons — the smoke and vapour a round leaves hanging in the air,
//     and the contrails, G streamers and gold ribbon a plane leaves behind.
//     Each trail is a chain of segments in one shared buffer, every point
//     stamped with the moment it was laid; the GPU ages them (the smoke
//     widens, drifts downwind, rises, thins away), so a trail costs the CPU
//     one write each time it grows and nothing afterwards. Joints share their
//     point, its age and its tangent, so a trail is one unbroken rope — no
//     beads where segments overlap, no gaps where they don't. A soft noise
//     texture breaks its edges into billows, and it is lit by the sky's own
//     sun and fill, as a tube is: white on its sunward side at noon, slate at
//     night, gold at dusk.
//
// Nothing here knows what a shell, a rocket or a plane is; effects.js says
// what to draw. Everything is instanced: one draw call per batch (two for
// the trails: the segments laid, and each trail's fresh stretch up to its
// round), whatever the weight of fire.

import * as THREE from "three";

// --- shared frame state -------------------------------------------------------

/**
 * Uniforms every streak and ribbon reads, updated once a frame by
 * updateStreakFrame(): the drawing buffer's size (the capsules are built in
 * pixels), the sky's light, and the soft noise the smoke is cut from.
 */
export const FX_UNIFORMS = {
  uViewport: { value: new THREE.Vector2(1280, 720) },
  uAmb: { value: new THREE.Color(0.7, 0.85, 1.1) }, // sky fill: hemisphere plus ambient
  uSun: { value: new THREE.Color(2.4, 2.15, 1.7) }, // key light: sun (or moon) times its strength
  uSunDir: { value: new THREE.Vector3(0.3, 0.9, 0.3).normalize() }, // toward the key light
  uCamRight: { value: new THREE.Vector3(1, 0, 0) },
  uNoise: { value: null }, // filled on first use (noiseTexture)
  uBehind: { value: new THREE.Color(0x8ec9ff) }, // the sky behind the fire (scene.background), in linear light
  // The veil (setStreakVeil): the smoke drums a sightline can cross.
  uVeil: { value: Array.from({ length: 8 }, () => new THREE.Vector4()) }, // xyz: a drum's foot; w: how thick it stands
  uVeilN: { value: 0 },
  uVeilShape: { value: new THREE.Vector3(1, 1, 0) }, // x: reach (m), y: height (m), z: optical depth a metre
};

// Tier 2 films the frame (ACES, gfx2.js), which rolls a sunlit white down to
// a light grey; smoke is lit this much harder under tone mapping, as the
// clouds are, so noon vapour stays white.
const TONE_LIFT = 1.3;

const _v = new THREE.Vector2();
const _c = new THREE.Color();

/**
 * Point the shared uniforms at this frame: `renderer` for the drawing
 * buffer's size and whether the light is filmed, `sky` (buildSky's handle)
 * for the sun, hemisphere and ambient, `camera` for the fallback axis of a
 * ribbon seen dead end-on, `scene` for the sky colour the glows lay their
 * light over. Any of them may be missing (the unit tests).
 */
export function updateStreakFrame({ renderer = null, sky = null, camera = null, scene = null } = {}) {
  if (renderer) {
    renderer.getDrawingBufferSize(_v);
    if (_v.x > 0 && _v.y > 0) FX_UNIFORMS.uViewport.value.copy(_v);
  }
  if (sky?.parts) {
    const { sun, hemi, ambient } = sky.parts;
    const lift = renderer && renderer.toneMapping !== THREE.NoToneMapping ? TONE_LIFT : 1;
    FX_UNIFORMS.uSun.value.copy(sun.color).multiplyScalar(sun.intensity * lift);
    FX_UNIFORMS.uAmb.value
      .copy(hemi.color)
      .multiplyScalar(hemi.intensity * lift)
      .add(_c.copy(ambient.color).multiplyScalar(ambient.intensity * lift));
    const d = FX_UNIFORMS.uSunDir.value.copy(sun.position);
    if (sun.target) d.sub(sun.target.position);
    if (d.lengthSq() > 1e-8) d.normalize();
  }
  if (scene?.background?.isColor) FX_UNIFORMS.uBehind.value.copy(scene.background);
  if (camera) {
    camera.updateMatrixWorld();
    FX_UNIFORMS.uCamRight.value.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
  }
}

// --- the noise the smoke is cut from -------------------------------------------

// A deterministic integer hash (no Math.random: every client, every reload,
// the same billows).
function hash2i(x, y, s) {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(s, 2147483647)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// Tileable value noise: lattice `cells` across the tile, smoothly blended.
function valueNoise(u, v, cells, seed) {
  const x = u * cells, y = v * cells;
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const fx = x - x0, fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const w = (i) => ((i % cells) + cells) % cells; // wrap: the tile repeats seamlessly
  const a = hash2i(w(x0), w(y0), seed), b = hash2i(w(x0 + 1), w(y0), seed);
  const c = hash2i(w(x0), w(y0 + 1), seed), d = hash2i(w(x0 + 1), w(y0 + 1), seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

/**
 * The soft noise the trails and wakes are cut from: a 128 px tile that
 * repeats seamlessly, red a billowy four-octave cloud, green a finer grain.
 * Built once, on first use, from a fixed seed.
 */
export function noiseTexture() {
  if (FX_UNIFORMS.uNoise.value) return FX_UNIFORMS.uNoise.value;
  const N = 128;
  const data = new Uint8Array(N * N * 4);
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const u = i / N, v = j / N;
      let billow = 0, grain = 0, amp = 0.5, cells = 4;
      for (let o = 0; o < 4; o++) {
        billow += amp * valueNoise(u, v, cells, 11 + o);
        amp *= 0.5;
        cells *= 2;
      }
      amp = 0.5;
      cells = 16;
      for (let o = 0; o < 3; o++) {
        grain += amp * valueNoise(u, v, cells, 71 + o);
        amp *= 0.5;
        cells *= 2;
      }
      const k = (j * N + i) * 4;
      data[k] = Math.round(Math.min(1, billow / 0.9375) * 255);
      data[k + 1] = Math.round(Math.min(1, grain / 0.875) * 255);
      data[k + 2] = 0;
      data[k + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  FX_UNIFORMS.uNoise.value = tex;
  return tex;
}

// --- the veil -------------------------------------------------------------------

// Everything drawn here goes on after the see-through bodies that stand in
// the world (a smoke screen's bank draws first), so a tracer stream running
// behind a bank would lay its light straight over the bank's face. The veil
// is how much of each streak and ribbon still shows through: whoever owns
// the smoke hands in its drums (setStreakVeil), and every vertex measures
// the stretch of its sightline that runs through one, and gives up that
// much of its strength. It's what the flipbook cards get from the same
// drums on the CPU (effects.js Smoke.transmit), done here on the GPU
// because a ribbon is laid once and aged there: it has no CPU write each
// frame to scale.

// How many drums a sightline is measured through: more screens than lie
// between an eye and anything it can see. With more on the water, the
// nearest to the eye.
export const VEIL_MAX = FX_UNIFORMS.uVeil.value.length;

/**
 * Hand the veil its drums: `drums` is [[x, footY, z, k], …] (k 0..1, how
 * thick the drum stands), each `reach` metres round and `height` tall, its
 * smoke `perM` optical depth a metre at full thickness. With more than
 * VEIL_MAX, the ones nearest `eye` are kept. No drums, no veil.
 */
export function setStreakVeil(drums, reach, height, perM, eye = null) {
  let list = drums;
  if (drums.length > VEIL_MAX && eye) {
    const d2 = (b) => (b[0] - eye.x) ** 2 + (b[2] - eye.z) ** 2;
    list = drums.slice().sort((a, b) => d2(a) - d2(b));
  }
  const n = Math.min(list.length, VEIL_MAX);
  for (let i = 0; i < n; i++) FX_UNIFORMS.uVeil.value[i].set(list[i][0], list[i][1], list[i][2], list[i][3]);
  FX_UNIFORMS.uVeilN.value = n;
  FX_UNIFORMS.uVeilShape.value.set(reach, height, perM);
}

// The veil in the shader: a vertex's sightline from the eye, clipped to
// each drum (a round wall, then its floor and lid), the smoke it crosses
// summed and taken off as extinction. Line for line what streakVeilAt
// does below.
const FX_VEIL_GLSL = /* glsl */ `
uniform vec4 uVeil[${VEIL_MAX}];
uniform int uVeilN;
uniform vec3 uVeilShape;
float fxVeil(vec3 p) {
  if (uVeilN == 0) return 1.0;
  vec3 scl = vec3(uVeilShape.x, uVeilShape.y, uVeilShape.x);
  vec3 d = p - cameraPosition;
  float len = length(d);
  vec3 u = d / scl;
  float tau = 0.0;
  for (int i = 0; i < ${VEIL_MAX}; i++) {
    if (i >= uVeilN) break;
    vec4 b = uVeil[i];
    vec3 o = (cameraPosition - b.xyz) / scl;
    float s0 = 0.0, s1 = 1.0;
    float qa = dot(u.xz, u.xz), qb = dot(o.xz, u.xz), qc = dot(o.xz, o.xz) - 1.0;
    if (qa > 1e-12) {
      float disc = qb * qb - qa * qc;
      if (disc <= 0.0) continue;
      float q = sqrt(disc);
      s0 = max(s0, (-qb - q) / qa);
      s1 = min(s1, (-qb + q) / qa);
    } else if (qc > 0.0) continue;
    if (abs(u.y) > 1e-12) {
      float ta = -o.y / u.y, tb = (1.0 - o.y) / u.y;
      s0 = max(s0, min(ta, tb));
      s1 = min(s1, max(ta, tb));
    } else if (o.y < 0.0 || o.y > 1.0) continue;
    if (s1 > s0) tau += (s1 - s0) * len * b.w;
  }
  return exp(-tau * uVeilShape.z);
}
`;

/**
 * The veil as the shader takes it: how much of something at (x, y, z)
 * shows to an eye at `eye`, 0..1. For the tests and probes; the draws
 * themselves run FX_VEIL_GLSL.
 */
export function streakVeilAt(eye, x, y, z) {
  const n = FX_UNIFORMS.uVeilN.value;
  if (n === 0) return 1;
  const S = FX_UNIFORMS.uVeilShape.value;
  const R = S.x, H = S.y;
  const dx = x - eye.x, dy = y - eye.y, dz = z - eye.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const ux = dx / R, uy = dy / H, uz = dz / R;
  let tau = 0;
  for (let i = 0; i < n; i++) {
    const b = FX_UNIFORMS.uVeil.value[i];
    const ox = (eye.x - b.x) / R, oy = (eye.y - b.y) / H, oz = (eye.z - b.z) / R;
    let s0 = 0, s1 = 1;
    const qa = ux * ux + uz * uz, qb = ox * ux + oz * uz, qc = ox * ox + oz * oz - 1;
    if (qa > 1e-12) {
      const disc = qb * qb - qa * qc;
      if (disc <= 0) continue;
      const q = Math.sqrt(disc);
      s0 = Math.max(s0, (-qb - q) / qa);
      s1 = Math.min(s1, (-qb + q) / qa);
    } else if (qc > 0) continue;
    if (Math.abs(uy) > 1e-12) {
      const ta = -oy / uy, tb = (1 - oy) / uy;
      s0 = Math.max(s0, Math.min(ta, tb));
      s1 = Math.min(s1, Math.max(ta, tb));
    } else if (oy < 0 || oy > 1) continue;
    if (s1 > s0) tau += (s1 - s0) * len * b.w;
  }
  return Math.exp(-tau * S.z);
}

// --- StreakBatch ----------------------------------------------------------------

// The quad every streak and ribbon segment is drawn on: x across (-1 / +1),
// y along (0 = tail or first point, 1 = head or second point).
function quadGeometry() {
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute("corner", new THREE.Float32BufferAttribute([-1, 0, 1, 0, -1, 1, 1, 1], 2));
  g.setIndex([0, 1, 2, 2, 1, 3]);
  // `position` only so three knows the vertex count; the shader places them.
  g.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(12), 3));
  // three sorts see-through draws by the middle of this sphere; the owner
  // moves it every frame to where its nearest streak is (sortAt).
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
  return g;
}

function instanced(g, name, n, size) {
  const a = new THREE.InstancedBufferAttribute(new Float32Array(n * size), size);
  a.setUsage(THREE.DynamicDrawUsage);
  g.setAttribute(name, a);
  return a;
}

const STREAK_VERT = /* glsl */ `
attribute vec2 corner;  // x: across (-1 | +1), y: end (0 tail | 1 head)
attribute vec4 iHead;   // xyz: head (world); w: radius there (m)
attribute vec4 iTail;   // xyz: tail (world); w: radius there (m)
attribute vec4 iColor;  // rgb: colour; a: strength (glow) or opacity (soft)
uniform vec2 uViewport; // the drawing buffer, px
uniform float uMinPx;   // no streak is drawn thinner than this radius, px
uniform float uMaxPx;   // or fatter than this one (a round brushing the lens)
uniform float uFadePow; // how a clamped streak gives back its strength
${FX_VEIL_GLSL}
varying vec2 vLocal;    // this fragment in the streak's own frame: along from the tail, across
varying vec3 vShape;    // length, tail radius, head radius (same units as vLocal)
varying vec4 vColor;
#ifdef FLAT
varying vec2 vWorld;    // where on the sea this fragment lies (the foam's noise)
#endif
#include <fog_pars_vertex>

void main() {
  bool atHead = corner.y > 0.5;
#ifdef FLAT
  // Lies on the water: built in the world, across the level heading.
  vec3 h = iHead.xyz, t = iTail.xyz;
  vec3 ax = h - t;
  ax.y = 0.0;
  float L = length(ax);
  vec3 dir = L > 1e-3 ? ax / L : vec3(1.0, 0.0, 0.0);
  vec3 nrm = vec3(-dir.z, 0.0, dir.x);
  float r = atHead ? iHead.w : iTail.w;
  float ext = atHead ? r : -r;
  vec3 wp = (atHead ? h : t) + nrm * (corner.x * r) + dir * ext;
  vLocal = vec2((atHead ? L : 0.0) + ext, corner.x * r);
  vShape = vec3(L, iTail.w, iHead.w);
  vColor = vec4(iColor.rgb, iColor.a * fxVeil(wp));
  vWorld = wp.xz;
  vec4 mvPosition = viewMatrix * vec4(wp, 1.0);
  gl_Position = projectionMatrix * mvPosition;
#else
  // Faces the eye: built on the screen, in pixels.
  vec4 h = viewMatrix * vec4(iHead.xyz, 1.0);
  vec4 t = viewMatrix * vec4(iTail.xyz, 1.0);
  const float NEAR = -0.55; // just past the camera's 0.5 m near plane
  float rh0 = iHead.w, rt0 = iTail.w;
  if (h.z > NEAR && t.z > NEAR) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0); // wholly behind the eye
    return;
  }
  if (h.z > NEAR) {
    float k = (NEAR - t.z) / (h.z - t.z);
    h.xyz = mix(t.xyz, h.xyz, k);
    rh0 = mix(rt0, rh0, k);
  } else if (t.z > NEAR) {
    float k = (NEAR - h.z) / (t.z - h.z);
    t.xyz = mix(h.xyz, t.xyz, k);
    rt0 = mix(rh0, rt0, k);
  }
  vec4 hc = projectionMatrix * h;
  vec4 tc = projectionMatrix * t;
  vec2 halfV = 0.5 * uViewport;
  vec2 hs = hc.xy / hc.w * halfV;
  vec2 ts = tc.xy / tc.w * halfV;
  float focal = projectionMatrix[1][1] * halfV.y; // px per metre at a metre's depth
  float rh = rh0 * focal / hc.w;
  float rt = rt0 * focal / tc.w;
  float rhc = clamp(rh, uMinPx, uMaxPx);
  float rtc = clamp(rt, uMinPx, uMaxPx);
  vec2 ax = hs - ts;
  float L = length(ax);
  vec2 dir = L > 1e-3 ? ax / L : vec2(1.0, 0.0);
  vec2 nrm = vec2(-dir.y, dir.x);
  float r = atHead ? rhc : rtc;
  float ext = atHead ? r : -r;
  vec2 p = (atHead ? hs : ts) + nrm * (corner.x * r) + dir * ext;
  float z = atHead ? hc.z / hc.w : tc.z / tc.w;
  // w = 1: the quad is already on the screen, so its varyings run straight
  // across it (and the depth along a line is linear on the screen anyway).
  gl_Position = vec4(p / halfV, z, 1.0);
  vLocal = vec2((atHead ? L : 0.0) + ext, corner.x * r);
  vShape = vec3(L, rtc, rhc);
  // A streak held at the minimum width is lit as if it were its true size:
  // it dims instead of fattening, so far fire reads as sparks, not bars.
  float cover = pow(clamp((rh + rt) / (rhc + rtc), 0.0, 1.0), uFadePow);
  // Veiled end by end: a stream of fire running into a bank dims along its
  // length as it goes in.
  float veil = fxVeil(atHead ? iHead.xyz : iTail.xyz);
  vColor = vec4(iColor.rgb, iColor.a * cover * veil);
  vec4 mvPosition = atHead ? h : t;
#endif
  #include <fog_vertex>
}
`;

// Into the draw target's own colour space, BEFORE the coverage is applied.
// Tier 1 draws straight onto the sRGB canvas, where blending happens between
// encoded colours: the gas is encoded straight and then laid on by its
// coverage, as every see-through material three draws is. Encoding the
// premultiplied value instead lit every thin edge up like a halo (the curve
// is steepest near black), so a translucent puff wore a bright rim. Light is
// encoded the same way, its own brightness taken as its coverage. Tier 2's
// linear target encodes nothing, and there this is exactly the
// premultiplied sum.
//
// A glow streak's halo is light over sky, and there even that split lies:
// what the eye should get is the sky and the light summed in linear light,
// then encoded — which is what tier 2 draws. Tier 1 can't read the pixel it
// lands on, but it knows the sky it's most likely laid over (the scene's
// flat background), so fxGlow writes exactly the encoded difference the
// light makes to THAT sky once the blend has kept (1 - a) of it. Over a
// black night it is the plain encode (nothing to lose to the curve); over a
// noon sky it is small, because a sunlit blue has little headroom left to
// brighten, and a faint fringe stops wrapping the tracer in a fat white bar.
// Over the ground it errs as little as the fxLight split does by day and far
// less by night. On tier 2 the two terms cancel to the premultiplied light.
export const FX_OUT_GLSL = /* glsl */ `
vec3 fxOut(vec3 c) {
  #ifdef TONE_MAPPING
    c = toneMapping(c);
  #endif
  return linearToOutputTexel(vec4(c, 1.0)).rgb;
}
vec3 fxLight(vec3 l) {
  float m = clamp(max(l.r, max(l.g, l.b)), 0.0, 1.0);
  return m > 1e-5 ? fxOut(l / m) * m : vec3(0.0);
}
vec3 fxGlow(vec3 l, float a, vec3 behind) {
  float keep = 1.0 - a;
  return max(fxOut(behind * keep + l) - fxOut(behind) * keep, 0.0);
}
`;

const STREAK_FRAG = /* glsl */ `
uniform vec3 uGain;     // the material's colour (tier 2 lifts the glows into HDR)
uniform vec4 uProfile;  // x: core radius (fraction of the rim), y: core gain, z: halo gain, w: tail strength
uniform vec3 uAmb;
uniform vec3 uSun;
#ifdef FLAT
uniform sampler2D uNoise;
varying vec2 vWorld;
#endif
#ifdef GLOW
uniform vec3 uBehind;
${FX_OUT_GLSL}
#endif
varying vec2 vLocal;
varying vec3 vShape;
varying vec4 vColor;
#include <fog_pars_fragment>

void main() {
  // Distance from the capsule's spine, in rims: 0 on it, 1 at its edge.
  float L = vShape.x;
  float s = clamp(vLocal.x / max(L, 1e-3), 0.0, 1.0); // 0 tail .. 1 head
  float r = max(mix(vShape.y, vShape.z, s), 1e-4);
  float d = length(vec2(vLocal.x - s * L, vLocal.y)) / r;
  if (d >= 1.0) discard;
  // Brighter toward the head — for a streak. A disc (a star shell, a
  // tracer seen dead end-on) has no head or tail, and splitting it into
  // two brightnesses would seam it down the middle.
  float along = L > 0.5 ? mix(uProfile.w, 1.0, s * s * (3.0 - 2.0 * s)) : 1.0;
#if defined(GLOW)
  // Light: a white-hot core in a coloured halo. Premultiplied, so the halo
  // adds its colour over a dark sky and still tints a bright one (a pure
  // additive glow washes out to white against noon blue).
  float core = exp(-d * d / (uProfile.x * uProfile.x));
  float halo = 1.0 - d * d;
  halo *= halo * halo;
  float k = vColor.a * along;
  vec3 col = (vColor.rgb * (halo * uProfile.z) + vec3(1.0, 0.95, 0.84) * (core * uProfile.y)) * k;
  float a = clamp((halo * 0.8 + core * 0.6) * k, 0.0, 1.0);
  gl_FragColor = vec4(fxGlow(col * uGain, a, uBehind), a); // already in the target's colour space
#elif defined(FLAT)
  // Foam on the sea: broken up by the noise, thinning aft.
  float n = texture2D(uNoise, vWorld * 0.045).r * 0.65 + texture2D(uNoise, vWorld * 0.19).g * 0.35;
  float prof = 1.0 - d * d;
  float foam = smoothstep(0.28, 0.72, n + (prof - 0.5) * 0.7);
  float a = vColor.a * along * prof * mix(0.35, 1.0, foam);
  if (a < 0.003) discard;
  vec3 col = vColor.rgb * (uAmb * 0.72 + uSun * 0.24);
  gl_FragColor = vec4(col * uGain, a);
#else
  // Vapour: lit by the sky, soft to its edges.
  float prof = 1.0 - d * d;
  prof *= prof;
  float a = vColor.a * prof * along;
  if (a < 0.002) discard;
  vec3 col = vColor.rgb * (uAmb * 0.62 + uSun * 0.26);
  gl_FragColor = vec4(col * uGain, a);
#endif
#ifndef GLOW
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
#endif
  #include <fog_fragment>
}
`;

// The glow's shape: a tight white-hot core (its radius a fraction of the
// streak's), the coloured halo round it, and how bright the tail burns
// against the head. Tracers, shell tracers, motors and flares all share it;
// their colours and sizes tell them apart.
export const GLOW_PROFILE = { core: 0.22, coreGain: 1.35, haloGain: 0.8, tail: 0.35 };

/**
 * A batch of streaks rebuilt every frame: begin(), push() each one, end().
 * `mode` is "glow" (light), "soft" (lit vapour) or "flat" (foam on the sea).
 */
export class StreakBatch {
  constructor(scene, capacity, {
    mode = "glow",
    minPx = 1.1,
    maxPx = 220,
    fadePow = 0.6,
    profile = GLOW_PROFILE,
    fog = mode !== "glow",
  } = {}) {
    this.capacity = capacity;
    this.mode = mode;
    this.count = 0;
    const g = quadGeometry();
    this.iHead = instanced(g, "iHead", capacity, 4);
    this.iTail = instanced(g, "iTail", capacity, 4);
    this.iColor = instanced(g, "iColor", capacity, 4);
    g.instanceCount = 0;
    this.geometry = g;
    // The material's colour multiplies everything it draws: gfx2.js lifts a
    // glow's into HDR so the bloom finds it.
    this.color = new THREE.Color(1, 1, 1);
    const defines = mode === "glow" ? { GLOW: "" } : mode === "flat" ? { FLAT: "" } : {};
    if (mode === "flat") noiseTexture();
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
        uViewport: FX_UNIFORMS.uViewport,
        uAmb: FX_UNIFORMS.uAmb,
        uSun: FX_UNIFORMS.uSun,
        uNoise: FX_UNIFORMS.uNoise,
        uBehind: FX_UNIFORMS.uBehind,
        uVeil: FX_UNIFORMS.uVeil,
        uVeilN: FX_UNIFORMS.uVeilN,
        uVeilShape: FX_UNIFORMS.uVeilShape,
        uMinPx: { value: minPx },
        uMaxPx: { value: maxPx },
        uFadePow: { value: fadePow },
        uGain: { value: this.color },
        uProfile: { value: new THREE.Vector4(profile.core, profile.coreGain, profile.haloGain, profile.tail) },
      },
      defines,
      vertexShader: STREAK_VERT,
      fragmentShader: STREAK_FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide, // built on the screen: its winding is whatever the heading makes it
      fog,
    });
    if (mode === "glow") {
      // Premultiplied: rgb is light added, alpha how much of the sky behind
      // the halo stands in for.
      this.material.blending = THREE.CustomBlending;
      this.material.blendSrc = THREE.OneFactor;
      this.material.blendDst = THREE.OneMinusSrcAlphaFactor;
      this.material.blendSrcAlpha = THREE.OneFactor;
      this.material.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
    }
    this.material.color = this.color; // gfx2.js's emitter hook multiplies .color
    this.mesh = new THREE.Mesh(g, this.material);
    this.mesh.frustumCulled = false; // streaks span the map; the quad sits at the origin
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  /** Start a frame's worth of streaks. */
  begin() {
    this.count = 0;
  }

  /**
   * One streak from tail (tx..) to head (hx..), `hr`/`tr` its radius at each
   * end in metres, (r, g, b) its colour and `a` its strength. Returns false
   * once the batch is full.
   */
  push(hx, hy, hz, hr, tx, ty, tz, tr, r, g, b, a) {
    const i = this.count;
    if (i >= this.capacity) return false;
    const H = this.iHead.array, T = this.iTail.array, C = this.iColor.array;
    const k = i * 4;
    H[k] = hx; H[k + 1] = hy; H[k + 2] = hz; H[k + 3] = hr;
    T[k] = tx; T[k + 1] = ty; T[k + 2] = tz; T[k + 3] = tr;
    C[k] = r; C[k + 1] = g; C[k + 2] = b; C[k + 3] = a;
    this.count = i + 1;
    return true;
  }

  /**
   * Finish the frame: upload what was pushed. `eye` (a camera position) lets
   * the batch sort among the other see-through draws at its nearest streak.
   */
  end(eye = null) {
    const n = this.count;
    for (const a of [this.iHead, this.iTail, this.iColor]) {
      a.clearUpdateRanges();
      if (n > 0) a.addUpdateRange(0, n * 4);
      a.needsUpdate = n > 0;
    }
    this.geometry.instanceCount = n;
    this.mesh.visible = n > 0;
    if (eye && n > 0) sortAt(this.geometry, eye, this.iHead.array, n, 4);
  }
}

// Move a batch's sort point to its nearest item: three orders see-through
// draws by the middle of each one's bounding sphere, and these span the map.
// Nearer than every cloud, it lays over them; beyond them all, it lies under
// (clouds.js sorts its decks the same way).
function sortAt(geometry, eye, arr, n, stride) {
  let best = Infinity, bx = 0, by = 0, bz = 0;
  for (let i = 0, k = 0; i < n; i++, k += stride) {
    const dx = arr[k] - eye.x, dy = arr[k + 1] - eye.y, dz = arr[k + 2] - eye.z;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < best) { best = d2; bx = arr[k]; by = arr[k + 1]; bz = arr[k + 2]; }
  }
  if (best < Infinity) geometry.boundingSphere.center.set(bx, by, bz);
}

// --- TrailRibbons ---------------------------------------------------------------

/**
 * How each kind of trail lives and dies, in metres and seconds:
 *   w0, grow, wMax — width at birth, how fast it spreads, where it stops
 *   life           — seconds until it has thinned to nothing
 *   alpha          — its density at birth; fadeIn — how long it takes to thicken
 *   rise           — buoyant climb (m/s); wind — how much of the wind it rides
 *   color          — its colour in full light; billow — how ragged its edges
 *   segM, segSec   — a new joint every segM metres or segSec seconds
 *   hang           — 0: thins from the start (smoke); 1: holds its density
 *                    most of its life, then thins away (a contrail)
 *   glow           — 0: lit by the sky; 1: its own light (the ace's gold,
 *                    which has to read at dusk and in the dark)
 *   near           — it thins away within this many metres of the eye (at
 *                    least four of its own widths): your own contrail
 *                    passes a few metres under the chase camera
 */
export const TRAIL_STYLES = {
  // A main-battery round's condensation trail: a thin chalk line hung along
  // the arc, the thing a captain reads a salvo's fall by.
  shellVapor: { w0: 0.4, grow: 0.9, wMax: 3.2, life: 2.8, alpha: 0.42, fadeIn: 0.06, rise: 0, wind: 0.6, color: 0xf2f5f8, billow: 0.35, segM: 120, segSec: 0.15 },
  // A tank round's: finer and shorter-lived than a battleship's, but
  // enough of a line that a gunner can walk the next round off the last.
  tankVapor: { w0: 0.2, grow: 0.6, wMax: 1.5, life: 1.8, alpha: 0.4, fadeIn: 0.04, rise: 0, wind: 0.5, color: 0xeef1f4, billow: 0.3, segM: 100, segSec: 0.12 },
  // A missile's motor smoke: the long white finger pointing at whoever it's
  // chasing — the counterplay. Dense, billowing, and it hangs.
  missileSmoke: { w0: 1.2, grow: 2.0, wMax: 12, life: 7, alpha: 0.85, fadeIn: 0.05, rise: 0.25, wind: 1, color: 0xf0efeb, billow: 0.7, segM: 60, segSec: 0.12 },
  // A rocket's: greyer, shorter, torn up faster.
  rocketSmoke: { w0: 1.0, grow: 2.4, wMax: 8, life: 3.6, alpha: 0.78, fadeIn: 0.04, rise: 0.4, wind: 1, color: 0xb9b6ae, billow: 0.8, segM: 20, segSec: 0.1 },
  // A star shell's: a fat white plume that climbs off it as it sinks.
  flareSmoke: { w0: 1.2, grow: 1.4, wMax: 9, life: 6, alpha: 0.6, fadeIn: 0.15, rise: 1.6, wind: 1, color: 0xf4f2ee, billow: 0.8, segM: 6, segSec: 0.4 },
  // A jet's decoy's: by day the smoke IS the flare — a thick white rope
  // curling off each one, the fan of them hanging where the jet broke.
  decoySmoke: { w0: 1.4, grow: 3.2, wMax: 12, life: 5, alpha: 0.85, fadeIn: 0.04, rise: 0.5, wind: 1, color: 0xf7f6f3, billow: 0.7, segM: 10, segSec: 0.1 },
  // A plane's contrail above the band floor (roadmap #45): condenses a few
  // plane-lengths behind the engine (the fade-in), a tight white thread
  // that spreads into a soft band and hangs for most of its life before it
  // thins — the high fight written across the blue for the whole map. Your
  // own runs a few metres under the chase camera, so it thins away within
  // `near` of the lens instead of smearing two beams across the screen.
  contrail: { w0: 0.9, grow: 0.5, wMax: 12, life: 22, alpha: 0.58, fadeIn: 0.3, rise: 0, wind: 1, color: 0xffffff, billow: 0.5, segM: 36, segSec: 0.2, hang: 0.7, near: 18 },
  // A wingtip vortex in a hard pull: a thin white line snapping off each
  // tip, gone in well under a second.
  streamer: { w0: 0.2, grow: 1.1, wMax: 1.1, life: 0.7, alpha: 0.6, fadeIn: 0.03, rise: 0, wind: 0.2, color: 0xffffff, billow: 0.2, segM: 12, segSec: 0.08, hang: 0.4 },
  // The comeback ace's ribbon (roadmap #14): gold that carries its own
  // light and glitters, so the bounty reads across the sky at any hour.
  aceGold: { w0: 1.2, grow: 0.8, wMax: 3.6, life: 3.5, alpha: 0.85, fadeIn: 0.05, rise: 0, wind: 0.4, color: 0xffc93a, billow: 0.3, segM: 12, segSec: 0.08, hang: 0.6, glow: 0.8 },
  // A hit engine's smoke: a dark rope pouring off the airframe, billowing
  // wider as it hangs and drifting downwind — the wounded bird's tell,
  // readable across a furball. Your own streams back under the chase
  // camera, so it thins away within `near` of the lens.
  damageSmoke: { w0: 1.1, grow: 2.4, wMax: 13, life: 4.5, alpha: 0.82, fadeIn: 0.05, rise: 0.5, wind: 1, color: 0x3d3b39, billow: 0.85, segM: 10, segSec: 0.08, near: 14 },
  // Fuel venting from a holed tank: a thin pale streak, spreading and gone
  // in a couple of seconds.
  fuelVapor: { w0: 0.45, grow: 1.5, wMax: 5, life: 2.2, alpha: 0.5, fadeIn: 0.05, rise: 0.1, wind: 1, color: 0xf2f4f6, billow: 0.45, segM: 10, segSec: 0.08, near: 14 },
};
export const TRAIL_KIND = Object.fromEntries(Object.keys(TRAIL_STYLES).map((k, i) => [k, i]));
const STYLE_NAMES = Object.keys(TRAIL_STYLES);
const STYLE_LIST = Object.values(TRAIL_STYLES);
// Vectors per style in the shader's table (see RIBBON_VERT).
const STYLE_VECS = 4;

// A sparse buffer this full lays its joints this much further apart: a long
// fight degrades to sparser trails before it has to shed any (see _commitAt).
const HOT_FILL = 0.8;
const HOT_SPREAD = 2;

const RIBBON_VERT = /* glsl */ `
attribute vec2 corner;  // x: side (-1 | +1), y: end (0 = point A, 1 = point B)
attribute vec4 iA;      // point A: xyz, the moment it was laid (s)
attribute vec4 iB;      // point B: xyz, the moment it was laid (s)
attribute vec4 iTA;     // the trail's heading at A, and how far along the trail A is (m)
attribute vec4 iTB;     // the same at B
attribute vec4 iMeta;   // x: style, y: size, z: seed, w: opacity
uniform float uTime;
uniform vec3 uWind;
uniform vec4 uStyle[${STYLE_LIST.length * STYLE_VECS}];
uniform vec2 uViewport;
uniform float uMinPx;
uniform vec3 uCamRight;
uniform vec3 uSunDir;
${FX_VEIL_GLSL}
varying vec2 vUv;       // x: across (-1..1), y: metres along the trail
varying float vAlpha;
varying vec3 vColor;
varying vec3 vLook;     // x: width (m), y: billow, z: seed
varying vec2 vShade;    // the key light across the tube (x) and toward the eye (y)
varying float vAge;
varying float vGlow;    // how much of its own light it carries
#include <fog_pars_vertex>

void main() {
  bool atB = corner.y > 0.5;
  vec4 P = atB ? iB : iA;
  vec4 T = atB ? iTB : iTA;
  int si = int(iMeta.x + 0.5) * ${STYLE_VECS};
  vec4 s0 = uStyle[si];     // w0, grow, wMax, life
  vec4 s1 = uStyle[si + 1]; // alpha, fadeIn, rise, wind
  vec4 s2 = uStyle[si + 2]; // colour, billow
  vec4 s3 = uStyle[si + 3]; // hang, glow, near
  float life = s0.w;
  float age = uTime - P.w;
  float ageO = uTime - (atB ? iA.w : iB.w);
  if (min(age, ageO) > life) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0); // gone: costs no fragments
    return;
  }
  age = max(age, 0.0);
  float k = clamp(age / life, 0.0, 1.0);
  float w = min(s0.x + s0.y * age, s0.z) * iMeta.y;
  float fade = 1.0 - k;
  // Smoke thins from the moment it's laid; a contrail holds most of its
  // density as it spreads, then thins away over the end of its life.
  float thin = mix(fade * fade, 1.0 - k * k * k, s3.x);
  float a = s1.x * iMeta.w * smoothstep(0.0, max(s1.y, 1e-3), age) * thin;
  // Smoke rides the wind and climbs as it ages; the joint moves as one, so
  // both segments that share it bend together.
  vec3 pos = P.xyz + uWind * (s1.w * age) + vec3(0.0, s1.z * age, 0.0);
  vec3 toCam = cameraPosition - pos;
  float dist = max(length(toCam), 1e-3);
  vec3 eye = toCam / dist;
  // Smoke the eye is about to fly into thins away rather than hanging as a
  // flat sheet across the lens.
  a *= smoothstep(0.6 * w, max(4.0 * w, s3.z), dist);
  // Smoke behind a smoke screen sinks into the bank, joint by joint.
  a *= fxVeil(pos);
  vec3 side = cross(T.xyz, eye);
  float sl = length(side);
  side = sl > 1e-3 ? side / sl : uCamRight; // seen dead end-on: any level axis will do
  // No thinner than uMinPx on the screen: a sub-pixel trail keeps a pixel's
  // width and gives up density for it, so a far salvo hangs a faint line,
  // not a crawl of dashes.
  float focal = projectionMatrix[1][1] * 0.5 * uViewport.y;
  float px = 0.5 * w * focal / dist;
  if (px < uMinPx) {
    a *= px / uMinPx;
    w *= uMinPx / max(px, 1e-4);
  }
  pos += side * (corner.x * 0.5 * w);
  vec4 mvPosition = viewMatrix * vec4(pos, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  vUv = vec2(corner.x, T.w);
  vAlpha = a;
  vColor = s2.rgb;
  vLook = vec3(w, s2.w, iMeta.z);
  vShade = vec2(dot(side, uSunDir), dot(eye, uSunDir));
  vAge = k;
  vGlow = s3.y;
  #include <fog_vertex>
}
`;

const RIBBON_FRAG = /* glsl */ `
uniform sampler2D uNoise;
uniform vec3 uAmb;
uniform vec3 uSun;
uniform vec3 uGain;
varying vec2 vUv;
varying float vAlpha;
varying vec3 vColor;
varying vec3 vLook;
varying vec2 vShade;
varying float vAge;
varying float vGlow;
#include <fog_pars_fragment>

void main() {
  float u = vUv.x;
  float w = max(vLook.x, 0.05);
  // The billows: noise stretched along the trail at the smoke's own scale
  // (a lump about as long as the rope is wide), seeded per trail and boiling
  // slowly as it ages; a finer grain rides on top.
  vec2 q = vec2(vUv.y / (w * 4.0) + vLook.z, u * 0.3 + vAge * 0.5 + vLook.z * 1.7);
  float n = texture2D(uNoise, q).r;
  float g = texture2D(uNoise, q * vec2(3.1, 2.3) + 0.37).g;
  float edge = abs(u) + (n - 0.5) * vLook.y + (g - 0.5) * vLook.y * 0.35;
  float prof = 1.0 - smoothstep(0.2, 1.0, edge);
  float dens = mix(1.0, 0.3 + 1.4 * n * (0.65 + 0.7 * g), vLook.y);
  float a = vAlpha * prof * dens;
  if (a < 0.003) discard;
  // Lit as a tube: the side facing the key light is bright, the far side in
  // its own shade, wrapped because smoke lets light through.
  float c = sqrt(max(1.0 - u * u, 0.0));
  float lambert = clamp((u * vShade.x + c * vShade.y + 0.6) / 1.6, 0.0, 1.0);
  vec3 col = vColor * (uAmb * 0.55 + uSun * (0.12 + 0.3 * lambert));
  if (vGlow > 0.0) {
    // Its own light, and a glitter of flecks in it that slide as it ages.
    float fleck = smoothstep(0.78, 0.96, texture2D(uNoise, q * vec2(6.3, 4.7) + vec2(vAge * 2.5, 0.61)).g);
    col = mix(col, vColor * (0.9 + 0.25 * c) + vec3(1.0, 0.92, 0.7) * fleck * 0.8, vGlow);
  }
  gl_FragColor = vec4(col * uGain, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

// A trail id's seed for its billows: a stable hash, so a trail keeps its
// shape frame to frame.
function seedOf(id) {
  return hash2i(id | 0, 7, 3) * 17.0;
}

/**
 * Every trail in the air: its segments packed oldest-first into one buffer
 * and aged on the GPU. Owners call feed(id, …) each frame for every live round
 * that leaves one, end(id, …) when the round is gone, and update(dt, wind)
 * once a frame.
 *
 * Only what can still be alive is drawn. Segments go in at the back in the
 * order they were laid, so the dead ones gather at the front; once enough
 * have, the survivors slide down over them in one move (and the live span is
 * uploaded once), and an empty sky draws nothing at all. That matters most
 * where it's hardest to see: a CPU rasterizer (SwiftShader, the e2e and a
 * blocklisted GPU's fallback) pays for every vertex a draw submits, dead or
 * not, and a buffer drawn whole cost it ~40 ms a frame with nothing alive.
 * The stretch from each trail's last joint up to its round — redrawn every
 * frame — is its own small mesh.
 *
 * `tune` overrides a style for this buffer alone, by name: the contrails
 * take their life from the config ({ contrail: { life: 22 } }). A `sparse`
 * buffer, running hot, lays its joints further apart before it has to shed
 * any: the contrails', whose 22 s life is what fills a buffer. `spread`
 * lays every joint that much further apart from the start (a CPU
 * rasterizer's lighter plane trails).
 */
export class TrailRibbons {
  constructor(scene, capacity = 4096, { heads = 256, minPx = 0.7, tune = {}, sparse = false, spread = 1 } = {}) {
    this.capacity = capacity;
    this.heads = heads;
    this.hotAt = sparse ? capacity * HOT_FILL : Infinity;
    this.spread = spread;
    this.time = 0;
    this.count = 0; // segments in the buffer, [0, count); the front ones may be dead
    this.written = 0; // segments ever laid (tests, probes)
    this.headN = 0;
    this._dirty = -1; // first slot written since the last upload (-1: none)
    this.live = new Map(); // id -> the trail's last joint and what it is
    this.styleList = STYLE_LIST.map((s, i) => ({ hang: 0, glow: 0, near: 0, ...s, ...tune[STYLE_NAMES[i]] }));
    this.styleLife = this.styleList.map((s) => s.life);
    const styles = [];
    for (const s of this.styleList) {
      const c = new THREE.Color(s.color);
      styles.push(
        new THREE.Vector4(s.w0, s.grow, s.wMax, s.life),
        new THREE.Vector4(s.alpha, s.fadeIn, s.rise, s.wind),
        new THREE.Vector4(c.r, c.g, c.b, s.billow),
        new THREE.Vector4(s.hang, s.glow, s.near, 0),
      );
    }
    this.color = new THREE.Color(1, 1, 1);
    noiseTexture();
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
        uTime: { value: 0 },
        uWind: { value: new THREE.Vector3() },
        uStyle: { value: styles },
        uViewport: FX_UNIFORMS.uViewport,
        uMinPx: { value: minPx },
        uCamRight: FX_UNIFORMS.uCamRight,
        uSunDir: FX_UNIFORMS.uSunDir,
        uNoise: FX_UNIFORMS.uNoise,
        uAmb: FX_UNIFORMS.uAmb,
        uSun: FX_UNIFORMS.uSun,
        uVeil: FX_UNIFORMS.uVeil,
        uVeilN: FX_UNIFORMS.uVeilN,
        uVeilShape: FX_UNIFORMS.uVeilShape,
        uGain: { value: this.color },
      },
      vertexShader: RIBBON_VERT,
      fragmentShader: RIBBON_FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: true,
    });
    const build = (n) => {
      const g = quadGeometry();
      const set = {
        g,
        iA: instanced(g, "iA", n, 4),
        iB: instanced(g, "iB", n, 4),
        iTA: instanced(g, "iTA", n, 4),
        iTB: instanced(g, "iTB", n, 4),
        iMeta: instanced(g, "iMeta", n, 4),
      };
      set.attrs = [set.iA, set.iB, set.iTA, set.iTB, set.iMeta];
      g.instanceCount = 0;
      const mesh = new THREE.Mesh(g, this.material);
      mesh.frustumCulled = false;
      mesh.visible = false;
      scene.add(mesh);
      set.mesh = mesh;
      return set;
    };
    this.segs = build(capacity);
    this.headSet = build(heads);
    // The buffer's names, for the owners, tests and probes.
    this.mesh = this.segs.mesh;
    this.headMesh = this.headSet.mesh;
    this.geometry = this.segs.g;
    this.iA = this.segs.iA;
    this.iB = this.segs.iB;
    this.iTA = this.segs.iTA;
    this.iTB = this.segs.iTB;
    this.iMeta = this.segs.iMeta;
  }

  // Write one segment into slot i of `set`: from joint s to the point (x, y, z).
  _write(set, i, s, x, y, z, t, tx, ty, tz, arc) {
    const k = i * 4;
    const A = set.iA.array, B = set.iB.array, TA = set.iTA.array, TB = set.iTB.array, M = set.iMeta.array;
    A[k] = s.x; A[k + 1] = s.y; A[k + 2] = s.z; A[k + 3] = s.t;
    B[k] = x; B[k + 1] = y; B[k + 2] = z; B[k + 3] = t;
    TA[k] = s.tx; TA[k + 1] = s.ty; TA[k + 2] = s.tz; TA[k + 3] = s.arc;
    TB[k] = tx; TB[k + 1] = ty; TB[k + 2] = tz; TB[k + 3] = arc;
    M[k] = s.style; M[k + 1] = s.scale; M[k + 2] = s.seed; M[k + 3] = s.opacity;
  }

  // Is the segment in slot i past its style's life at both ends?
  _dead(i) {
    const k = i * 4;
    return this.time - this.iB.array[k + 3] > this.styleLife[this.iMeta.array[k] | 0];
  }

  // Slide the segments from slot `k` on down to the front, dropping the k
  // before them, and mark the whole span for upload.
  _shift(k) {
    if (k <= 0) return;
    const n = this.count;
    for (const a of this.segs.attrs) a.array.copyWithin(0, k * 4, n * 4);
    this.count = Math.max(0, n - k);
    this._dirty = 0;
  }

  // Lay a segment at the back of the buffer. A full buffer first sheds its
  // dead; failing that, its oldest eighth (a long fight degrades to shorter
  // trails, never a frozen one). Before it comes to that, a sparse buffer
  // running hot lays its joints further apart (feed): sparser, not shorter.
  _commit(s, x, y, z, tx, ty, tz, arc) {
    this._commitAt(s, x, y, z, this.time, tx, ty, tz, arc);
  }

  // The same, for a joint laid at time t (a skipped joint put back in).
  _commitAt(s, x, y, z, t, tx, ty, tz, arc) {
    if (this.count >= this.capacity) {
      let k = 0;
      while (k < this.count && this._dead(k)) k++;
      this._shift(Math.max(k, this.capacity >> 3));
    }
    const i = this.count++;
    this._write(this.segs, i, s, x, y, z, t, tx, ty, tz, arc);
    this.written++;
    if (this._dirty < 0) this._dirty = i;
  }

  /**
   * A live round at (x, y, z) flying (vx, vy, vz) leaves trail `style` (a
   * TRAIL_KIND); `scale` sizes its width, `opacity` its density, from here
   * on (a contrail thickens as its plane climbs through the band floor).
   * The first call for an id starts its trail right there.
   */
  feed(id, x, y, z, vx, vy, vz, style, scale = 1, opacity = 1) {
    const sp = Math.sqrt(vx * vx + vy * vy + vz * vz);
    const inv = sp > 1e-4 ? 1 / sp : 0;
    const tx = sp > 1e-4 ? vx * inv : 0, ty = sp > 1e-4 ? vy * inv : 1, tz = sp > 1e-4 ? vz * inv : 0;
    let s = this.live.get(id);
    if (!s) {
      s = { x, y, z, t: this.time, tx, ty, tz, arc: 0, style, scale, opacity, seed: seedOf(id), fed: this.time };
      this.live.set(id, s);
      return;
    }
    s.fed = this.time;
    s.scale = scale;
    s.opacity = opacity;
    // Where it was last drawn: a trail whose round vanishes ends there.
    s.hx = x; s.hy = y; s.hz = z;
    const dx = x - s.x, dy = y - s.y, dz = z - s.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const st = this.styleList[s.style];
    const spread = this.spread * (this.count > this.hotAt ? HOT_SPREAD : 1);
    const segM = st.segM * spread;
    if (d >= segM || (this.time - s.t >= st.segSec * spread && d > 0.05)) {
      // A round that got far past its last joint in one frame (a slow
      // client, a hitch, a CPU rasterizer at a frame a second) would lay one
      // straight chord across its arc. Bend the stretch instead: the joints
      // it skipped go in along the curve that leaves the last joint on its
      // heading and meets the round on its own.
      const steps = Math.min(8, Math.floor(d / segM));
      if (steps > 1) {
        const t0 = s.t, a0 = s.arc;
        const ax = s.x, ay = s.y, az = s.z, m0x = s.tx * d, m0y = s.ty * d, m0z = s.tz * d;
        const m1x = tx * d, m1y = ty * d, m1z = tz * d;
        for (let k = 1; k < steps; k++) {
          const u = k / steps, u2 = u * u, u3 = u2 * u;
          const h00 = 2 * u3 - 3 * u2 + 1, h10 = u3 - 2 * u2 + u, h01 = 3 * u2 - 2 * u3, h11 = u3 - u2;
          const px = h00 * ax + h10 * m0x + h01 * x + h11 * m1x;
          const py = h00 * ay + h10 * m0y + h01 * y + h11 * m1y;
          const pz = h00 * az + h10 * m0z + h01 * z + h11 * m1z;
          // Its heading there: the curve's own slope.
          const g00 = 6 * u2 - 6 * u, g10 = 3 * u2 - 4 * u + 1, g01 = 6 * u - 6 * u2, g11 = 3 * u2 - 2 * u;
          let qx = g00 * ax + g10 * m0x + g01 * x + g11 * m1x;
          let qy = g00 * ay + g10 * m0y + g01 * y + g11 * m1y;
          let qz = g00 * az + g10 * m0z + g01 * z + g11 * m1z;
          const ql = Math.sqrt(qx * qx + qy * qy + qz * qz) || 1;
          qx /= ql; qy /= ql; qz /= ql;
          const arcK = a0 + d * u;
          const tK = t0 + (this.time - t0) * u;
          this._commitAt(s, px, py, pz, tK, qx, qy, qz, arcK);
          s.x = px; s.y = py; s.z = pz; s.t = tK;
          s.tx = qx; s.ty = qy; s.tz = qz; s.arc = arcK;
        }
      }
      const arc = s.arc + Math.sqrt((x - s.x) ** 2 + (y - s.y) ** 2 + (z - s.z) ** 2);
      this._commit(s, x, y, z, tx, ty, tz, arc);
      s.x = x; s.y = y; s.z = z; s.t = this.time;
      s.tx = tx; s.ty = ty; s.tz = tz; s.arc = arc;
    } else if (d > 0.01 && this.headN < this.heads) {
      // The stretch since the last joint, drawn fresh each frame so the
      // trail runs right up to the round.
      this._write(this.headSet, this.headN, s, x, y, z, this.time, tx, ty, tz, s.arc + d);
      this.headN++;
    }
  }

  /**
   * The round is gone: lay the last stretch — to (x, y, z), or to where it
   * was last drawn — and let the trail hang.
   */
  end(id, x = undefined, y = undefined, z = undefined) {
    const s = this.live.get(id);
    if (!s) return;
    this.live.delete(id);
    if (x === undefined) {
      if (s.hx === undefined) return; // never drawn past its first point
      x = s.hx; y = s.hy; z = s.hz;
    }
    const dx = x - s.x, dy = y - s.y, dz = z - s.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d > 0.05) this._commit(s, x, y, z, s.tx, s.ty, s.tz, s.arc + d);
  }

  /** Is there a trail running for this id? */
  has(id) {
    return this.live.has(id);
  }

  /**
   * Hang a whole trail at once, as if its emitter had just flown it: `pts`
   * is [x, y, z, age, x, y, z, age, …] from its oldest point to its newest,
   * each `age` how many seconds ago the emitter passed there. Every point
   * is a joint, headed toward its neighbours. The trail is laid finished
   * (nothing feeds it on); it sorts at its newest point until a live trail
   * says otherwise. For staging a still — the screenshot director's
   * contrails and gold — and for the tests.
   */
  lay(id, pts, style, scale = 1, opacity = 1) {
    const n = Math.floor(pts.length / 4);
    if (n < 2) return;
    const s = { x: pts[0], y: pts[1], z: pts[2], t: this.time - pts[3], tx: 0, ty: 1, tz: 0, arc: 0, style, scale, opacity, seed: seedOf(id), fed: this.time };
    // The heading at point i: from the point before it to the point after.
    const head = (i) => {
      const a = Math.max(0, i - 1) * 4, b = Math.min(n - 1, i + 1) * 4;
      const hx = pts[b] - pts[a], hy = pts[b + 1] - pts[a + 1], hz = pts[b + 2] - pts[a + 2];
      const l = Math.sqrt(hx * hx + hy * hy + hz * hz);
      return l > 1e-6 ? [hx / l, hy / l, hz / l] : [0, 1, 0];
    };
    [s.tx, s.ty, s.tz] = head(0);
    for (let i = 1; i < n; i++) {
      const k = i * 4;
      const x = pts[k], y = pts[k + 1], z = pts[k + 2], t = this.time - pts[k + 3];
      const [tx, ty, tz] = head(i);
      const arc = s.arc + Math.sqrt((x - s.x) ** 2 + (y - s.y) ** 2 + (z - s.z) ** 2);
      this._commitAt(s, x, y, z, t, tx, ty, tz, arc);
      s.x = x; s.y = y; s.z = z; s.t = t;
      s.tx = tx; s.ty = ty; s.tz = tz; s.arc = arc;
    }
    this.segs.g.boundingSphere.center.set(s.x, s.y, s.z);
    this.headSet.g.boundingSphere.center.set(s.x, s.y, s.z);
  }

  /**
   * Advance the smoke's clock by dt (the effects clock: slow-mo slows it),
   * blow it along (windX, windZ) m/s, and upload what changed. `eye` (a
   * camera position) sorts the ribbons among the other see-through draws.
   */
  update(dt, windX = 0, windZ = 0, eye = null) {
    // A trail nobody has fed for a second lost its round without an end():
    // forget it (its segments hang on and fade by themselves).
    for (const [id, s] of this.live) if (this.time - s.fed > 1) this.live.delete(id);
    // Shed the dead from the front once there are enough of them to be
    // worth a move — or all of them, when nothing's left alive.
    let k = 0;
    while (k < this.count && this._dead(k)) k++;
    if (k === this.count || k >= Math.max(64, this.count >> 2)) this._shift(k);
    const segs = this.segs, heads = this.headSet;
    for (const a of segs.attrs) {
      a.clearUpdateRanges();
      if (this._dirty >= 0 && this.count > this._dirty) a.addUpdateRange(this._dirty * 4, (this.count - this._dirty) * 4);
      a.needsUpdate = this._dirty >= 0 && this.count > this._dirty;
    }
    this._dirty = -1;
    segs.g.instanceCount = this.count;
    segs.mesh.visible = this.count > 0;
    for (const a of heads.attrs) {
      a.clearUpdateRanges();
      if (this.headN > 0) a.addUpdateRange(0, this.headN * 4);
      a.needsUpdate = this.headN > 0;
    }
    heads.g.instanceCount = this.headN;
    heads.mesh.visible = this.headN > 0;
    if (eye) this._sort(eye);
    this.headN = 0; // heads are redrawn every frame
    this.time += dt;
    this.material.uniforms.uTime.value = this.time;
    this.material.uniforms.uWind.value.set(windX, 0, windZ);
  }

  // Sort at the nearest live round (the freshest, densest smoke is where the
  // rounds are), or leave the last sort point where it was.
  _sort(eye) {
    let best = Infinity, bx = 0, by = 0, bz = 0;
    for (const s of this.live.values()) {
      const dx = s.x - eye.x, dy = s.y - eye.y, dz = s.z - eye.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < best) { best = d2; bx = s.x; by = s.y; bz = s.z; }
    }
    if (best < Infinity) {
      this.segs.g.boundingSphere.center.set(bx, by, bz);
      this.headSet.g.boundingSphere.center.set(bx, by, bz);
    }
  }
}

// --- the shell ---------------------------------------------------------------------

/**
 * A shell: ogive nose, parallel body, a copper-coloured driving band and a
 * boat tail, turned on a lathe and laid nose-forward along +Z (the pool
 * orients rounds with lookAt). `calibre` and `length` in metres.
 */
export function shellGeometry(calibre = 0.4, length = 1.8) {
  const r = calibre / 2;
  const pts = [];
  // Profile from the base (z = 0) to the nose (z = length), as (radius, z).
  const tail = length * 0.12, band = length * 0.18, body = length * 0.55;
  pts.push(new THREE.Vector2(0, 0));
  pts.push(new THREE.Vector2(r * 0.78, 0));
  pts.push(new THREE.Vector2(r, tail));
  pts.push(new THREE.Vector2(r * 1.04, tail + 0.001));
  pts.push(new THREE.Vector2(r * 1.04, band));
  pts.push(new THREE.Vector2(r, band + 0.001));
  pts.push(new THREE.Vector2(r, body));
  // The ogive: a tangent arc from the body to the tip.
  const ogive = length - body, steps = 7;
  for (let i = 1; i <= steps; i++) {
    const f = i / steps;
    pts.push(new THREE.Vector2(r * Math.sqrt(Math.max(0, 1 - f * f)) * (1 - 0.06 * f), body + ogive * f));
  }
  const g = new THREE.LatheGeometry(pts, 10);
  // Lathe turns about +Y; the pool points +Z at the heading. Centre it on
  // its middle so the round sits where the wire says it is.
  g.rotateX(Math.PI / 2);
  g.translate(0, 0, -length / 2);
  g.computeVertexNormals();
  return g;
}
